import { motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { fmtNum } from '@/utils/format';

interface CircularProgressProps {
  consumed: number;
  goal: number;
  size?: number;
  strokeWidth?: number;
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  color: string;
  life: number;
  maxLife: number;
}

export function CircularProgress({
  consumed,
  goal,
  size = 200,
  strokeWidth = 14,
}: CircularProgressProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const particlesRef = useRef<Particle[]>([]);

  const safeGoal = goal > 0 ? goal : 1;
  const percent = (consumed / safeGoal) * 100;

  const isOver = percent > 110;
  const isSignificantlyOver = percent > 130;

  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (Math.min(percent, 100) / 100) * circumference;

  // Приглушённый янтарь вместо неонового #fbbf24: перебор нормы должен
  // спокойно отмечаться, а не кричать. Один цвет на всё превышение —
  // здесь и в карточках БЖУ; дублируется как токен `over` в tailwind.config
  // (SVG и canvas ниже нужен сырой hex, классами не обойтись).
  const OVER = '#e8a85c';
  const color = isOver ? OVER : '#34d399';

  const particleColors = isOver
    ? [OVER, '#dd9a63']
    : ['#34d399', '#06b6d4'];

  // Ореол берёт цвет кольца. Раньше он был жёстко зелёным во всех ветках,
  // поэтому янтарная дуга светилась зелёным — вот это и читалось кислотой.
  // В переборе ореол ещё и слабее: тревожный цвет не надо разгонять.
  const glowBase = isOver ? 5 : 6;
  const glowPeak = isOver ? 10 : 14;
  const glowPeakAlpha = isOver ? 'cc' : 'ff';

  const remaining = goal - consumed;
  const surplus = consumed - goal;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const cx = size / 2;
    const cy = size / 2;
    const innerR = radius - strokeWidth / 2 - 4;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.scale(dpr, dpr);

    const PARTICLE_COUNT = 22;

    const createParticle = (initial = false): Particle => {
      const angle = Math.random() * Math.PI * 2;
      const r = Math.sqrt(Math.random()) * innerR;
      const colorIdx = Math.floor(Math.random() * particleColors.length);

      return {
        x: cx + Math.cos(angle) * r,
        y: initial
          ? cy + Math.sin(angle) * r
          : cy + innerR * 0.8 + Math.random() * 10,
        vx: (Math.random() - 0.5) * 0.15,
        vy: -0.2 - Math.random() * 0.3,
        size: 1 + Math.random() * 2,
        opacity: 0.3 + Math.random() * 0.4,
        color: particleColors[colorIdx],
        life: 0,
        maxLife: 300 + Math.random() * 200,
      };
    };

    if (particlesRef.current.length === 0) {
      particlesRef.current = Array.from({ length: PARTICLE_COUNT }, () => createParticle(true));
    } else {
      particlesRef.current.forEach(p => {
        const colorIdx = Math.floor(Math.random() * particleColors.length);
        p.color = particleColors[colorIdx];
      });
    }

    let lastTime = performance.now();

    const animate = (currentTime: number) => {
      if (document.visibilityState !== 'visible') {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }

      const dt = Math.min((currentTime - lastTime) / 16.67, 2);
      lastTime = currentTime;

      ctx.clearRect(0, 0, size, size);

      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
      ctx.clip();

      particlesRef.current.forEach((p, idx) => {
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.life += dt;

        p.vx += Math.sin(p.life * 0.05) * 0.005;
        p.vx = Math.max(-0.3, Math.min(0.3, p.vx));

        const lifeProgress = p.life / p.maxLife;
        let opacityMultiplier = 1;
        if (lifeProgress < 0.1) {
          opacityMultiplier = lifeProgress / 0.1;
        } else if (lifeProgress > 0.7) {
          opacityMultiplier = (1 - lifeProgress) / 0.3;
        }
        const currentOpacity = p.opacity * Math.max(0, opacityMultiplier);

        const dx = p.x - cx;
        const dy = p.y - cy;
        const distFromCenter = Math.sqrt(dx * dx + dy * dy);

        if (p.life >= p.maxLife || distFromCenter > innerR) {
          particlesRef.current[idx] = createParticle(false);
          return;
        }

        const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 3);
        glow.addColorStop(0, hexToRgba(p.color, currentOpacity));
        glow.addColorStop(0.5, hexToRgba(p.color, currentOpacity * 0.3));
        glow.addColorStop(1, 'transparent');

        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 3, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = hexToRgba(p.color, currentOpacity);
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      });

      ctx.restore();

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [size, radius, strokeWidth, isOver]);

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      {/* Canvas с частицами */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        style={{ width: size, height: size }}
      />

      {/* SVG-круг */}
      <svg width={size} height={size} className="-rotate-90 relative z-10" style={{ overflow: 'visible' }}>
        <defs>
          <filter id="ring-glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#1f2937"
          strokeWidth={strokeWidth}
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{
            strokeDashoffset: offset,
            filter: remaining > 0 && remaining < 200
              ? [
                  `drop-shadow(0 0 4px ${color}aa)`,
                  `drop-shadow(0 0 10px ${color}ff)`,
                  `drop-shadow(0 0 4px ${color}aa)`,
                ]
              : remaining <= 0
              ? [
                  `drop-shadow(0 0 ${glowBase}px ${color}cc)`,
                  `drop-shadow(0 0 ${glowPeak}px ${color}${glowPeakAlpha})`,
                  `drop-shadow(0 0 ${glowBase}px ${color}cc)`,
                ]
              : `drop-shadow(0 0 7px ${color}80)`,
          }}
          transition={{
            strokeDashoffset: { duration: 0.8, ease: 'easeOut' },
            filter: remaining > 0 && remaining < 200
              ? { duration: 1.6, repeat: Infinity, ease: 'easeInOut' }
              : remaining <= 0
              ? { duration: 2, repeat: Infinity, ease: 'easeInOut' }
              : { duration: 0.4 },
          }}
          strokeLinecap="round"
        />
      </svg>

      {/* Центр */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 z-20">
        <CenterDisplay
          remaining={remaining}
          surplus={surplus}
        />
      </div>
    </div>
  );
}

function CenterDisplay({
  remaining,
  surplus,
}: {
  remaining: number;
  surplus: number;
}) {
  // Состояние 1: есть остаток — показываем сколько осталось
  if (remaining > 0) {
    return (
      <>
        <motion.div
          key="remaining"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="font-display font-bold text-3xl text-emerald-glow"
          style={{ textShadow: '0 0 18px rgba(16,229,142,0.5)' }}
        >
          {fmtNum(remaining)}
        </motion.div>
        <div className="text-xs text-ink-dim uppercase tracking-wider mt-1">
          осталось
        </div>
      </>
    );
  }

  // Состояние 2: профицит > 100 ккал — цифра amber
  if (surplus > 100) {
    return (
      <motion.div
        key="surplus"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col items-center gap-1"
      >
        {/* Тот же приглушённый янтарь, что и у кольца — цифра стоит внутри него */}
        <div className="font-display font-bold text-3xl text-[#e8a85c]">
          +{fmtNum(surplus)}
        </div>
        <div className="text-[10px] text-[#e8a85c]/70 uppercase tracking-wider">
          сверх нормы
        </div>
      </motion.div>
    );
  }

  // Состояние 3: ровно выполнено или профицит ≤ 100 — галочка с пульсацией
  return (
    <motion.div
      key="done"
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 260, damping: 18 }}
      className="flex flex-col items-center gap-2"
    >
      <div className="relative flex items-center justify-center">
        {/* Внешний пульс */}
        <motion.div
          animate={{ scale: [1, 1.35, 1], opacity: [0.25, 0, 0.25] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute w-14 h-14 rounded-full bg-emerald-glow/20"
        />
        {/* Внутренний пульс */}
        <motion.div
          animate={{ scale: [1, 1.12, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="w-11 h-11 rounded-full bg-emerald-glow/15 flex items-center justify-center"
        >
          {/* Галочка с анимацией прорисовки */}
          <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
            <motion.path
              d="M5 13 L10.5 19 L21 7"
              stroke="#34d399"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2, ease: 'easeOut' }}
            />
          </svg>
        </motion.div>
      </div>

      <div className="text-[11px] text-emerald-glow/80 uppercase tracking-wider font-medium">
        цель выполнена
      </div>
    </motion.div>
  );
}
